import React, { useMemo } from 'react';
import {
  GlobalSettingItemFragment,
  MediaImageFragment,
  PageCommunityDetail,
  PageCommunityDetailFragment,
  PageCommunityStandard
} from '@src/graphql/gql-types';
import Layout from '@src/components/Layout';
import { ContentfulPageHyperlinkFragments } from '@src/types/contentful';
import { getPageUrlPath } from '@src/utils/url';
import SEO from '@src/components/SEO';
import {
  generateMetaTitle,
  getBreadcrumbs,
  getCommunityDetailsFromBreadcrumbs,
  getFooterNavItems,
  getIsLiveChatEnabledFromBreadcrumbs,
  getOgImage
} from '@src/utils/helpers';
import { createReplaceDictionary } from '@src/utils/strings';
import {
  getCommunitiesMegaMenuItemEntry,
  getFormEnquiryDisclaimerEntry,
  getGlobalContactEmailEntry,
  getGlobalContactPhoneNumberEntry,
  getGlobalCTAPanelEntry,
  getSiteWideAlertBannerEntry
} from '@src/utils/globalSettings';
import { CONTENTFUL_PAGE_CONTENT_TYPES } from '@src/constants/contentful';
import { CommunityStandardPageProps } from '@src/types/page';
import HeroSmall from '@src/components/HeroSmall';
import HtmlComment from '@src/components/HtmlComment';
import IntroText from '@src/components/IntroText';
import DiscoverBlock from '@src/components/DiscoverBlock';
import GenericText from '@src/components/GenericText';
import VcdLink from '@src/components/VcdLink';
import { SITE_NAME } from '@src/constants/site';
import { cloudinaryClient } from '@src/services/cloudinary';
import { useSetCommunityDetail } from '@src/hooks/useCommunityDetail';
import dynamic from 'next/dynamic';
import { transformCommunitiesByStateToHyperLinks } from '@src/utils/communityLocations';

const ContentBlocks = dynamic(() => import('@src/components/ContentBlocks'), {
  ssr: false
});

type PageCommunityStandardTemplate = CommunityStandardPageProps;

const PageCommunityStandardTemplate: React.FC<
  PageCommunityStandardTemplate
> = ({
  globalSettingsPayload,
  nationalBasePagePayload,
  communityBasePagePayload,
  pagePayload,
  pageContentBlocks,
  pageCommunityPropertiesPayload
}) => {
  const { page, pagePath } = pagePayload || {};
  const pageData = page?.items[0];
  const pagePathData = pagePath?.items?.[0] as ContentfulPageHyperlinkFragments;
  const breadcrumbs = getBreadcrumbs(pagePathData);

  const globalSettingItems = globalSettingsPayload?.globalSettings
    ?.items as GlobalSettingItemFragment[];

  const alertBanner = getSiteWideAlertBannerEntry(globalSettingItems);

  const villageComparisonDocumentPublicId =
    pageCommunityPropertiesPayload?.community?.items?.[0]
      ?.villageComparisonDocument?.cloudinarySource?.[0]?.public_id || '';
  const villageComparisonDocumentUrl = cloudinaryClient
    .image(villageComparisonDocumentPublicId)
    .toURL();

  const globalCTAPanel = getGlobalCTAPanelEntry(globalSettingItems);
  const formDisclaimerRichText =
    getFormEnquiryDisclaimerEntry(globalSettingItems);

  const {
    footerNav,
    legalLinks,
    primaryNav: communityPrimaryNav,
    socialLinks,
    communities
  } = communityBasePagePayload || {};

  const communitiesMegaMenuItem =
    getCommunitiesMegaMenuItemEntry(globalSettingItems);
  const communityNav = transformCommunitiesByStateToHyperLinks(
    communities?.items as PageCommunityDetail[],
    communitiesMegaMenuItem
  );

  const { title, seoTitle, seoDescription, noFollow, noIndex } = pageData ?? {};
  const disclaimer = pageData?.disclaimer;
  const publishedAt = pageData?.sys?.publishedAt;
  const heroSmall =
    pageData?.hero && pageData?.hero?.__typename === 'HeroSmall';
  const introText = pageData?.introText;
  const discoverBlock = pageData?.discoverBlock;
  const pageURLPath = getPageUrlPath(pagePathData);
  const contactSalesRepresentative =
    pageData?.parentPage?.__typename === 'PageCommunityDetail'
      ? pageData?.parentPage?.contactSalesRepresentative
      : undefined;
  const latLon =
    pageData?.parentPage?.__typename === 'PageCommunityDetail'
      ? pageData?.parentPage?.latLon
      : undefined;

  const footerNavItems = getFooterNavItems(
    communityPrimaryNav,
    footerNav,
    socialLinks,
    legalLinks
  );

  const isLiveChatEnabled = getIsLiveChatEnabledFromBreadcrumbs(breadcrumbs);
  const communityDetailsSegment =
    getCommunityDetailsFromBreadcrumbs(breadcrumbs);

  const metaTitle =
    seoTitle ??
    generateMetaTitle([title, communityDetailsSegment?.title, SITE_NAME]);

  const metaOgImage = getOgImage(
    pageData?.hero?.mediaItem as MediaImageFragment
  );

  const replacementDictionary = createReplaceDictionary(
    globalSettingItems,
    publishedAt
  );

  const communityItems = nationalBasePagePayload?.communities
    ?.items as PageCommunityDetailFragment[];

  const communityDetailData = useMemo(() => {
    const communityDetails = communityBasePagePayload?.communityDetail;

    return {
      slug: communityDetails?.slug ?? '',
      title: communityDetails?.title ?? '',
      state: (communityDetails?.state?.slug ?? '').toUpperCase()
    };
  }, [communityBasePagePayload]);

  // sets community details in context for use in lead forms
  useSetCommunityDetail(communityDetailData);

  return (
    <Layout
      formType={'community_enquiry'}
      alertBannerData={alertBanner}
      pageCommunityData={pageData as PageCommunityStandard}
      communityNav={communityNav}
      communitiesMegaMenuItem={communitiesMegaMenuItem}
      communityName={communityDetailsSegment?.title || pageData?.title}
      communitySlug={communityDetailsSegment?.slug}
      primaryNav={
        communityPrimaryNav?.items[0]?.navigationElementsCollection?.items
      }
      formDisclaimerRichText={formDisclaimerRichText}
      footerNav={footerNavItems}
      showLiveChat={isLiveChatEnabled}
      headOfficePhoneNumber={
        getGlobalContactPhoneNumberEntry(globalSettingItems)?.value
      }
      headOfficeEmail={getGlobalContactEmailEntry(globalSettingItems)?.value}
      contactSalesRepresentative={{
        ...contactSalesRepresentative,
        latLon: {
          lat: latLon?.lat,
          lon: latLon?.lon
        }
      }}
    >
      <SEO
        metaTitle={metaTitle}
        metaDesc={seoDescription || ''}
        metaPageUrl={pageURLPath}
        metaOgImage={metaOgImage?.url}
        metaOgImageAlt={metaOgImage?.alt}
        noIndex={noIndex}
        noFollow={noFollow}
      />

      <HtmlComment comment="PageCommunityStandard Template" />

      {heroSmall && (
        <HeroSmall
          {...pageData?.hero}
          replacementDictionary={replacementDictionary}
          isNewsEventsPage={pageData.slug === 'news-and-events'}
          isCommunityPage
        />
      )}

      {introText && (
        <IntroText
          introText={introText}
          replacementDictionary={replacementDictionary}
          showBottomPadding={
            pageContentBlocks?.[0]?.__typename !== 'AlternatingTextBlock'
          }
        />
      )}

      {pageContentBlocks && (
        <ContentBlocks
          items={pageContentBlocks}
          pageType={CONTENTFUL_PAGE_CONTENT_TYPES.COMMUNITY_STANDARD}
          staticBlockData={{
            globalCTAPanel,
            communityContactForm: {
              disclaimerRichText: formDisclaimerRichText,
              communityDetailsSegment: communityDetailsSegment
            },
            communityProperties: pageCommunityPropertiesPayload,
            ...(nationalBasePagePayload?.communities && {
              communities: communityItems || undefined
            }),
            primaryNav: communityPrimaryNav
          }}
          sharedBuildData={{
            replacementDictionary,
            communityName:
              communityDetailsSegment?.title || pageData?.title || '',
            communitySlug: communityDetailsSegment?.slug
          }}
        />
      )}

      {/* Only show villageComparisonDocumentUrl on the for-sale pages */}
      {pageData?.slug === 'for-sale' && villageComparisonDocumentUrl && (
        <VcdLink documentUrl={villageComparisonDocumentUrl} />
      )}

      {discoverBlock && <DiscoverBlock {...discoverBlock} />}

      {disclaimer?.bodyContent && (
        <GenericText
          disclaimer
          {...disclaimer}
          pageType={CONTENTFUL_PAGE_CONTENT_TYPES.NATIONAL_STANDARD}
          sharedBuildData={{
            replacementDictionary,
            communityName:
              communityDetailsSegment?.title || pageData?.title || '',
            communitySlug: communityDetailsSegment?.slug
          }}
        />
      )}
    </Layout>
  );
};

export default PageCommunityStandardTemplate;
